import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MessageSquare, Settings, FileText, Plus, Edit, Trash2, Save, 
  AlertCircle, CheckCircle, Link, Phone, User, Building, 
  Send, Eye, RefreshCw, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Intent icons mapping
const INTENT_ICONS = {
  project_details: Building,
  brochure: FileText,
  payment_plan: FileText,
  floor_plan: FileText,
  project_link: Link,
  broker_registration: User,
  callback: Phone,
  meeting: User,
  general_info: MessageSquare,
};

export default function WhatsAppFollowupSettings() {
  const [activeTab, setActiveTab] = useState('settings');
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [availableIntents, setAvailableIntents] = useState([]);
  const [sendModes, setSendModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Template dialog state
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    code: '',
    type: 'general',
    message: '',
    variables: [],
    is_active: true
  });
  
  // Rule dialog state
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    intent: '',
    template_id: '',
    enabled: true,
    priority: 1
  });
  
  // Lead Source Rules state (Additive Feature)
  const [leadSourceRules, setLeadSourceRules] = useState([]);
  const [leadSourceDialogOpen, setLeadSourceDialogOpen] = useState(false);
  const [editingLeadSourceRule, setEditingLeadSourceRule] = useState(null);
  const [leadSourceForm, setLeadSourceForm] = useState({
    name: '',
    lead_source: '',
    template_id: '',
    send_immediately: true,
    enabled: true
  });
  
  const token = sessionStorage.getItem('auth_token');

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    if (!token) {
      toast.error('Please login to access this page');
      return;
    }
    try {
      setLoading(true);
      const [settingsRes, templatesRes, leadSourceRes] = await Promise.all([
        axios.get(`${API}/api/whatsapp-followup/settings?token=${token}`),
        axios.get(`${API}/api/whatsapp-followup/templates?token=${token}`),
        axios.get(`${API}/api/whatsapp-followup/lead-source-rules?token=${token}`)
      ]);
      
      if (settingsRes.data.success) {
        setSettings(settingsRes.data.settings);
        setAvailableIntents(settingsRes.data.available_intents || []);
        setSendModes(settingsRes.data.send_modes || []);
      }
      
      if (templatesRes.data.success) {
        setTemplates(templatesRes.data.templates || []);
      }
      
      if (leadSourceRes.data.success) {
        setLeadSourceRules(leadSourceRes.data.rules || []);
      }
    } catch (error) {
      console.error('Failed to fetch data', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (enabled) => {
    try {
      setSaving(true);
      await axios.put(`${API}/api/whatsapp-followup/settings?token=${token}`, { enabled });
      setSettings(prev => ({ ...prev, enabled }));
      toast.success(enabled ? 'WhatsApp follow-up enabled' : 'WhatsApp follow-up disabled');
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleSendModeChange = async (send_mode) => {
    try {
      setSaving(true);
      await axios.put(`${API}/api/whatsapp-followup/settings?token=${token}`, { send_mode });
      setSettings(prev => ({ ...prev, send_mode }));
      toast.success('Send mode updated');
    } catch (error) {
      toast.error('Failed to update send mode');
    } finally {
      setSaving(false);
    }
  };

  // Template CRUD
  const openTemplateDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name,
        code: template.code,
        type: template.type || 'general',
        message: template.message,
        variables: template.variables || [],
        is_active: template.is_active !== false
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        code: '',
        type: 'general',
        message: '',
        variables: [],
        is_active: true
      });
    }
    setTemplateDialogOpen(true);
  };

  const saveTemplate = async () => {
    try {
      setSaving(true);
      
      // Extract variables from message
      const variableMatches = templateForm.message.match(/\{\{(\w+)\}\}/g) || [];
      const variables = variableMatches.map(v => v.replace(/\{\{|\}\}/g, ''));
      
      const data = { ...templateForm, variables };
      
      if (editingTemplate) {
        await axios.put(`${API}/api/whatsapp-followup/templates/${editingTemplate.id}?token=${token}`, data);
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/api/whatsapp-followup/templates?token=${token}`, data);
        toast.success('Template created');
      }
      
      setTemplateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/api/whatsapp-followup/templates/${templateId}?token=${token}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const createDefaultTemplates = async () => {
    try {
      setSaving(true);
      const response = await axios.post(`${API}/api/whatsapp-followup/templates/create-defaults?token=${token}`);
      if (response.data.success) {
        toast.success(`Created ${response.data.created_count} default templates`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create default templates');
    } finally {
      setSaving(false);
    }
  };

  // Rule CRUD
  const openRuleDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setRuleForm({
        intent: rule.intent,
        template_id: rule.template_id || '',
        enabled: rule.enabled !== false,
        priority: rule.priority || 1
      });
    } else {
      setEditingRule(null);
      setRuleForm({
        intent: '',
        template_id: '',
        enabled: true,
        priority: 1
      });
    }
    setRuleDialogOpen(true);
  };

  const saveRule = async () => {
    try {
      setSaving(true);
      
      if (editingRule) {
        await axios.put(`${API}/api/whatsapp-followup/rules/${editingRule.id}?token=${token}`, ruleForm);
        toast.success('Rule updated');
      } else {
        await axios.post(`${API}/api/whatsapp-followup/rules?token=${token}`, ruleForm);
        toast.success('Rule created');
      }
      
      setRuleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      await axios.delete(`${API}/api/whatsapp-followup/rules/${ruleId}?token=${token}`);
      toast.success('Rule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const toggleRuleEnabled = async (rule) => {
    try {
      await axios.put(`${API}/api/whatsapp-followup/rules/${rule.id}?token=${token}`, {
        ...rule,
        enabled: !rule.enabled
      });
      toast.success(rule.enabled ? 'Rule disabled' : 'Rule enabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  // Lead Source Rule CRUD (Additive Feature)
  const openLeadSourceDialog = (rule = null) => {
    if (rule) {
      setEditingLeadSourceRule(rule);
      setLeadSourceForm({
        name: rule.name || '',
        lead_source: rule.lead_source || '',
        template_id: rule.template_id || '',
        send_immediately: rule.send_immediately !== false,
        enabled: rule.enabled !== false
      });
    } else {
      setEditingLeadSourceRule(null);
      setLeadSourceForm({
        name: '',
        lead_source: '',
        template_id: '',
        send_immediately: true,
        enabled: true
      });
    }
    setLeadSourceDialogOpen(true);
  };

  const saveLeadSourceRule = async () => {
    try {
      setSaving(true);
      
      if (editingLeadSourceRule) {
        await axios.put(`${API}/api/whatsapp-followup/lead-source-rules/${editingLeadSourceRule.id}?token=${token}`, leadSourceForm);
        toast.success('Lead source rule updated');
      } else {
        await axios.post(`${API}/api/whatsapp-followup/lead-source-rules?token=${token}`, leadSourceForm);
        toast.success('Lead source rule created');
      }
      
      setLeadSourceDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save lead source rule');
    } finally {
      setSaving(false);
    }
  };

  const deleteLeadSourceRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this lead source rule?')) return;
    
    try {
      await axios.delete(`${API}/api/whatsapp-followup/lead-source-rules/${ruleId}?token=${token}`);
      toast.success('Lead source rule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete lead source rule');
    }
  };

  const toggleLeadSourceRuleEnabled = async (rule) => {
    try {
      await axios.put(`${API}/api/whatsapp-followup/lead-source-rules/${rule.id}?token=${token}`, {
        enabled: !rule.enabled
      });
      toast.success(rule.enabled ? 'Rule disabled' : 'Rule enabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const getIntentName = (intentId) => {
    const intent = availableIntents.find(i => i.id === intentId);
    return intent?.name || intentId;
  };

  const getTemplateName = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    return template?.name || 'Not configured';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Call Follow-up Actions</h1>
          <p className="text-gray-500 mt-1">
            Configure automatic WhatsApp follow-ups based on AI call conversations
          </p>
        </div>
        <Badge variant={settings?.enabled ? "default" : "secondary"} className="text-sm">
          {settings?.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </div>

      {/* Main Toggle */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Enable AI Conversation Based WhatsApp Follow-up</CardTitle>
                <CardDescription>
                  Automatically send WhatsApp messages based on detected intents from AI calls
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={settings?.enabled || false}
              onCheckedChange={handleToggleEnabled}
              disabled={saving}
            />
          </div>
        </CardHeader>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex items-center gap-2">
            <ChevronRight className="h-4 w-4" />
            Intent Rules
          </TabsTrigger>
          <TabsTrigger value="lead-source" className="flex items-center gap-2">
            <Building className="h-4 w-4" />
            Lead Source
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Templates
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Auto Send Mode</CardTitle>
              <CardDescription>
                Choose how follow-up messages should be handled after AI calls
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                {sendModes.map((mode) => (
                  <div
                    key={mode.id}
                    onClick={() => handleSendModeChange(mode.id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      settings?.send_mode === mode.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        settings?.send_mode === mode.id 
                          ? 'border-blue-500 bg-blue-500' 
                          : 'border-gray-300'
                      }`}>
                        {settings?.send_mode === mode.id && (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{mode.name}</p>
                        <p className="text-sm text-gray-500">{mode.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Intent Mapping Rules</CardTitle>
                  <CardDescription>
                    Map AI conversation intents to WhatsApp templates
                  </CardDescription>
                </div>
                <Button onClick={() => openRuleDialog()} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Rule
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {settings?.rules?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No rules configured yet</p>
                    <Button variant="outline" size="sm" className="mt-2" onClick={() => openRuleDialog()}>
                      Create your first rule
                    </Button>
                  </div>
                ) : (
                  settings?.rules?.map((rule, index) => {
                    const IntentIcon = INTENT_ICONS[rule.intent] || MessageSquare;
                    return (
                      <div
                        key={rule.id || index}
                        className={`flex items-center justify-between p-4 border rounded-lg ${
                          rule.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <IntentIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{getIntentName(rule.intent)}</p>
                            <p className="text-sm text-gray-500">
                              Template: {getTemplateName(rule.template_id)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={rule.enabled}
                            onCheckedChange={() => toggleRuleEnabled(rule)}
                          />
                          <Button variant="ghost" size="sm" onClick={() => openRuleDialog(rule)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteRule(rule.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lead Source Rules Tab (Additive Feature) */}
        <TabsContent value="lead-source" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Lead Source Mapping</CardTitle>
                  <CardDescription>
                    Map lead sources to WhatsApp templates for automatic follow-up
                  </CardDescription>
                </div>
                <Button onClick={() => openLeadSourceDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Rule
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leadSourceRules.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Building className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No lead source rules configured</p>
                    <p className="text-sm mt-1">
                      Create rules to send WhatsApp templates based on lead source
                    </p>
                    <Button variant="outline" size="sm" className="mt-4" onClick={() => openLeadSourceDialog()}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create first rule
                    </Button>
                  </div>
                ) : (
                  leadSourceRules.map((rule) => (
                    <div
                      key={rule.id}
                      className={`flex items-center justify-between p-4 border rounded-lg ${
                        rule.enabled ? 'bg-white' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Building className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">{rule.name}</p>
                          <p className="text-sm text-gray-500">
                            Source: <span className="font-medium text-purple-600">{rule.lead_source}</span>
                          </p>
                          <p className="text-sm text-gray-500">
                            Template: {getTemplateName(rule.template_id)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={rule.send_immediately ? "default" : "secondary"} className="text-xs">
                          {rule.send_immediately ? 'Immediate' : 'Queued'}
                        </Badge>
                        <Switch
                          checked={rule.enabled}
                          onCheckedChange={() => toggleLeadSourceRuleEnabled(rule)}
                        />
                        <Button variant="ghost" size="sm" onClick={() => openLeadSourceDialog(rule)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteLeadSourceRule(rule.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Help Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">How Lead Source Mapping Works</p>
                  <p className="text-sm text-blue-700 mt-1">
                    When an AI call completes, the system checks if the lead's source matches any active rule.
                    If a match is found and "Send Immediately" is enabled, the mapped WhatsApp template is sent automatically.
                    This works alongside intent-based rules - both can trigger for the same call.
                  </p>
                  <p className="text-sm text-blue-700 mt-2">
                    <strong>Example sources:</strong> Treppan Serequie, Treppan Tower, Maimoon Gardens
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>WhatsApp Templates</CardTitle>
                  <CardDescription>
                    Manage message templates with dynamic variables
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={createDefaultTemplates} disabled={saving}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
                    Create Defaults
                  </Button>
                  <Button onClick={() => openTemplateDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Template
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {templates.length === 0 ? (
                  <div className="col-span-2 text-center py-8 text-gray-500">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No templates created yet</p>
                    <Button variant="outline" size="sm" className="mt-2" onClick={createDefaultTemplates}>
                      Create default templates
                    </Button>
                  </div>
                ) : (
                  templates.map((template) => (
                    <Card key={template.id} className={!template.is_active ? 'opacity-60' : ''}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-base">{template.name}</CardTitle>
                            <Badge variant="outline" className="mt-1">{template.type}</Badge>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openTemplateDialog(template)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteTemplate(template.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 line-clamp-3">{template.message}</p>
                        {template.variables?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {template.variables.map((v, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {`{{${v}}}`}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
            <DialogDescription>
              Use variables like {'{{lead_name}}'}, {'{{project_name}}'}, {'{{brochure_link}}'} in your message
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Project Details Template"
                />
              </div>
              <div className="space-y-2">
                <Label>Template Code</Label>
                <Input
                  value={templateForm.code}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="e.g., project_details"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={templateForm.type}
                onValueChange={(value) => setTemplateForm(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="broker">Broker</SelectItem>
                  <SelectItem value="followup">Follow-up</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Message Content</Label>
              <Textarea
                value={templateForm.message}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Hello {{lead_name}}, thank you for your interest..."
                rows={6}
              />
              <p className="text-xs text-gray-500">
                Available variables: {'{{lead_name}}'}, {'{{project_name}}'}, {'{{project_summary}}'}, 
                {'{{brochure_link}}'}, {'{{payment_plan_link}}'}, {'{{registration_link}}'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={templateForm.is_active}
                onCheckedChange={(checked) => setTemplateForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplate} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rule Dialog */}
      <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Rule' : 'Create Rule'}</DialogTitle>
            <DialogDescription>
              Map an AI conversation intent to a WhatsApp template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Intent / Trigger</Label>
              <Select
                value={ruleForm.intent}
                onValueChange={(value) => setRuleForm(prev => ({ ...prev, intent: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select intent" />
                </SelectTrigger>
                <SelectContent>
                  {availableIntents.map((intent) => (
                    <SelectItem key={intent.id} value={intent.id}>
                      {intent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>WhatsApp Template</Label>
              <Select
                value={ruleForm.template_id}
                onValueChange={(value) => setRuleForm(prev => ({ ...prev, template_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.is_active).map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={ruleForm.priority}
                onChange={(e) => setRuleForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 1 }))}
              />
              <p className="text-xs text-gray-500">Lower number = higher priority</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={ruleForm.enabled}
                onCheckedChange={(checked) => setRuleForm(prev => ({ ...prev, enabled: checked }))}
              />
              <Label>Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveRule} disabled={saving || !ruleForm.intent}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Source Rule Dialog (Additive Feature) */}
      <Dialog open={leadSourceDialogOpen} onOpenChange={setLeadSourceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingLeadSourceRule ? 'Edit Lead Source Rule' : 'Create Lead Source Rule'}
            </DialogTitle>
            <DialogDescription>
              Map a lead source to automatically send a WhatsApp template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rule Name</Label>
              <Input
                placeholder="e.g., Treppan Serequie Follow-up"
                value={leadSourceForm.name}
                onChange={(e) => setLeadSourceForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Lead Source</Label>
              <Input
                placeholder="e.g., Treppan Serequie, Maimoon Gardens"
                value={leadSourceForm.lead_source}
                onChange={(e) => setLeadSourceForm(prev => ({ ...prev, lead_source: e.target.value }))}
              />
              <p className="text-xs text-gray-500">
                Partial matching is supported (e.g., "Treppan" matches "Treppan Serequie")
              </p>
            </div>
            <div className="space-y-2">
              <Label>WhatsApp Template</Label>
              <Select
                value={leadSourceForm.template_id}
                onValueChange={(value) => setLeadSourceForm(prev => ({ ...prev, template_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.is_active).map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>Send Immediately</Label>
                <p className="text-xs text-gray-500">Send WhatsApp as soon as AI call completes</p>
              </div>
              <Switch
                checked={leadSourceForm.send_immediately}
                onCheckedChange={(checked) => setLeadSourceForm(prev => ({ ...prev, send_immediately: checked }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={leadSourceForm.enabled}
                onCheckedChange={(checked) => setLeadSourceForm(prev => ({ ...prev, enabled: checked }))}
              />
              <Label>Rule Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeadSourceDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={saveLeadSourceRule} 
              disabled={saving || !leadSourceForm.name || !leadSourceForm.lead_source || !leadSourceForm.template_id}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
